/**
 * `window.theme.*` — shared utility surface consumed across PDP modules
 * and other sections that opt in. Single home for cross-module helpers
 * so we don't fragment into one-function asset files. Add new helpers
 * here when they need to be reachable from more than one snippet /
 * block / section's `{% javascript %}` block.
 *
 * Currently exposes:
 *   - `window.theme.loadProductJson(handle)` — in-memory cache for
 *     `/products/{handle}.js` payloads so the PDP store,
 *     `find-in-store.js`, and the colour-variant cards section share a
 *     single fetch instead of issuing parallel requests.
 *   - `window.theme.formatMoney(amountCents, format?)` — token-replacing
 *     money formatter. `format` defaults to `window.theme.moneyFormat`
 *     when omitted; ultimate fallback is a plain `$1.23` string.
 *   - `window.theme.cartQueue(task)` — serialises cart writes so only one
 *     mutation is ever in flight. Shopify's Ajax cart locks during a write
 *     and returns 409 Conflict when two mutations overlap, so every ATC /
 *     cart-change surface routes through here to avoid racing.
 *   - `window.theme.cartAdd(body, options?)` — queued POST to
 *     `/cart/add.js` with a bounded retry on 409. Resolves with the parsed
 *     cart-add payload; rejects with an Error carrying `.status` + `.body`.
 *
 * Loaded globally from `theme.liquid` so every ATC surface (PDP, product
 * cards, quick add, wishlist, gift-with-purchase) can rely on these
 * helpers being on `window.theme` by the time their handlers fire.
 */
(function () {
  /* Drop variants hidden from the Online Store channel from a payload
     before it reaches a caller. The shared cache always holds the RAW
     payload (channel visibility is per buyer-context, not per handle), so
     the filter is applied to the returned view only. FAIL-OPEN: with no
     `excludeIds`, or before `variantUtils` has loaded, the payload is
     returned untouched — a no-op for an all-online product. */
  function applyChannelFilter(payload, excludeIds) {
    if (!excludeIds) return payload;
    var utils = window.theme && window.theme.variantUtils;
    if (!utils || typeof utils.filterHiddenVariants !== 'function') return payload;
    return utils.filterHiddenVariants(payload, excludeIds);
  }

  function loadProductJson(handle, excludeIds) {
    if (window.__productCache && window.__productCache[handle]) {
      return Promise.resolve(applyChannelFilter(window.__productCache[handle], excludeIds));
    }
    return fetch(`/products/${handle}.js`, { credentials: 'same-origin' })
      .then((res) => res.json())
      .then((payload) => {
        window.__productCache = window.__productCache || {};
        window.__productCache[handle] = payload;
        return applyChannelFilter(payload, excludeIds);
      });
  }

  function formatMoney(amountCents, format) {
    const value = (amountCents / 100).toFixed(2);
    const template = format || (window.theme && window.theme.moneyFormat);
    if (!template) return `$${value}`;
    return template
      .replace(/\{\{\s*amount\s*\}\}/g, value)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(amountCents / 100));
  }

  /* One-at-a-time cart-mutation queue. Chains each task onto the previous
     so only a single write is ever in flight; a task's own rejection never
     stalls the queue (the chain swallows it while the caller still sees the
     real result / error via the returned promise). */
  const noop = () => {};
  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  let cartChain = Promise.resolve();

  function cartQueue(task) {
    const run = cartChain.then(task, task);
    cartChain = run.then(noop, noop);
    return run;
  }

  /* POST /cart/add.js with a bounded retry on 409 — even a serialised
     client can still collide with a write from another tab or an app, so a
     short backed-off retry lets those self-heal instead of surfacing an
     error. Any non-409 failure rejects immediately with the parsed body and
     status attached. */
  function postAdd(body, attempt, maxRetries, headers) {
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers,
      body,
    }).then((res) =>
      res.json().then((payload) => {
        if (res.ok) return payload;
        if (res.status === 409 && attempt < maxRetries) {
          return delay(150 + attempt * 200).then(() => postAdd(body, attempt + 1, maxRetries));
        }
        const message = (payload && (payload.description || payload.message)) || 'unknown';
        const err = new Error(`Cart add failed (${res.status}): ${message}`);
        err.status = res.status;
        err.body = payload;
        throw err;
      })
    );
  }

  function cartAdd(body, options) {
    const opts = options || {};
    const maxRetries = opts.retries == null ? 2 : opts.retries;
    /* A JSON string body needs an explicit content-type; a FormData body
       sets its own multipart boundary, so leave it alone. Callers can still
       override via options.headers. */
    const headers = { Accept: 'application/json' };
    if (typeof body === 'string') headers['Content-Type'] = 'application/json';
    Object.assign(headers, opts.headers || {});
    return cartQueue(() => postAdd(body, 0, maxRetries, headers));
  }

  /* ─── GA4 tracking helpers ──────────────────────────────────────────
     The theme publishes storefront events via Shopify.analytics.publish
     under the `eckersleys:` namespace; a Web Pixel relays them to GA4. */

  /* Fire-and-forget publish. Instrumentation must never break a
     storefront interaction, so any failure is swallowed. */
  function publish(name, data) {
    try {
      const analytics = window.Shopify && window.Shopify.analytics;
      if (analytics && typeof analytics.publish === 'function') {
        analytics.publish(name, data || {});
      }
    } catch (e) {
      /* no-op */
    }
  }

  /* Invoke `onEnter` once, the first time `el` is at least `threshold`
     visible, then stop observing — a list/promotion impression fires once
     per instance. Falls back to an immediate call without IntersectionObserver. */
  function observeOnce(el, onEnter, options) {
    if (!el || typeof onEnter !== 'function') return;
    const threshold = options && options.threshold != null ? options.threshold : 0.5;
    if (!('IntersectionObserver' in window)) {
      onEnter();
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          onEnter();
        });
      },
      { threshold }
    );
    observer.observe(el);
  }

  /* Build one canonical GA4 item from a product card's `data-item-*`
     attributes. `index` is its position in the list so it matches the
     rendered order. */
  function trackingItem(el, index, listId, listName) {
    if (!el) return null;
    const d = el.dataset;
    const price = parseFloat(d.itemPrice);
    const item = {
      index: index || 0,
      quantity: 1,
      item_id: d.itemId || null,
      item_name: d.itemName || '',
      item_brand: d.itemBrand || '',
      item_variant: d.itemVariant || '',
      item_category: d.itemCategory || '',
      affiliation: 'Online Store',
    };
    if (Number.isFinite(price)) item.price = price;
    if (listId) item.item_list_id = listId;
    if (listName) item.item_list_name = listName;
    return item;
  }

  /* Build an items[] array from a NodeList/array of card roots, in order. */
  function trackingItems(els, listId, listName) {
    return Array.from(els || [])
      .map((el, index) => trackingItem(el, index, listId, listName))
      .filter(Boolean);
  }

  /* Resolve the list identity a card belongs to from the nearest ancestor
     carrying `data-track-list-id` (carousels, related lists, PLP grid,
     search). Returns null when the card is not inside a tracked list. */
  function resolveTrackList(el) {
    const listEl = el && el.closest('[data-track-list-id]');
    if (!listEl) return null;
    return {
      el: listEl,
      id: listEl.dataset.trackListId,
      name: listEl.dataset.trackListName || '',
    };
  }

  /* Observe a list wrapper and publish `eckersleys:view_item_list` once it
     enters the viewport. Idempotent — safe to call on load AND after a
     surface injects its cards asynchronously (recommendations). */
  function trackList(listEl) {
    if (!listEl || listEl.dataset.trackObserved === 'true') return;
    const id = listEl.dataset.trackListId;
    if (!id) return;
    listEl.dataset.trackObserved = 'true';
    /* Collection + search grids set `data-track-list-noview` because the
       pixel fires view_item_list for them natively (collection_viewed /
       search_submitted) — the list identity is stamped only to power
       select_item / atc_context, so skip the impression observer here. */
    if (listEl.dataset.trackListNoview === 'true') return;
    const name = listEl.dataset.trackListName || '';
    observeOnce(listEl, () => {
      const items = trackingItems(listEl.querySelectorAll('[data-product-card]'), id, name);
      if (!items.length) return;
      publish('eckersleys:view_item_list', {
        item_list_id: id,
        item_list_name: name,
        items,
      });
    });
  }

  /* Publish `eckersleys:select_item` for a single card, resolving its list
     identity and position from the surrounding tracked list. */
  function trackSelectItem(cardEl) {
    const list = resolveTrackList(cardEl);
    if (!list) return;
    const cards = list.el.querySelectorAll('[data-product-card]');
    const index = Array.prototype.indexOf.call(cards, cardEl);
    const item = trackingItem(cardEl, index < 0 ? 0 : index, list.id, list.name);
    if (!item) return;
    const currency = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
    publish('eckersleys:select_item', {
      item_list_id: list.id,
      item_list_name: list.name,
      currency: currency || undefined,
      value: item.price,
      items: [item],
    });
  }

  /* Publish `eckersleys:atc_context` just before a quick-add mutation, so
     the pixel can attribute the resulting add_to_cart to this list.
     No-op when the card is not in a tracked list. */
  function trackAtcContext(cardEl, variantId) {
    const list = resolveTrackList(cardEl);
    if (!list) return;
    publish('eckersleys:atc_context', {
      item_list_id: list.id,
      item_list_name: list.name,
      variant_id: variantId || null,
    });
  }

  /* ─── Store trading hours ───────────────────────────────────────────
     Shared by the PDP find-in-store drawer and the store finder, both of
     which read the `store_location` metaobject's `opening_hours` (JSON
     keyed by lowercase day name, each `{ open: 'HH:MM', close: 'HH:MM',
     closed?: true }`).

     Both helpers exist because a store's trading state has to be read on
     the STORE's clock, not the viewer's and not the shop's. Eckersley's
     spans AEST, ACST and (from October) a QLD/NSW split when daylight
     saving starts in every state except Queensland. `Intl` owns that;
     we never do offset or DST arithmetic. */

  /* AU state/territory -> IANA zone. The single table for the whole
     theme: both hours surfaces resolve through here, and the Liquid that
     builds the PDP hours map deliberately emits `state_code` rather than
     a zone so this stays the only copy.

     Sydney and Melbourne are the same clock; they are listed separately
     because collapsing them reads as a bug. Unknown or blank state falls
     back to Sydney — the largest part of the network. */
  const STATE_ZONES = {
    ACT: 'Australia/Sydney',
    NSW: 'Australia/Sydney',
    NT: 'Australia/Darwin',
    QLD: 'Australia/Brisbane',
    SA: 'Australia/Adelaide',
    TAS: 'Australia/Hobart',
    VIC: 'Australia/Melbourne',
    WA: 'Australia/Perth',
  };

  function zoneForState(stateCode) {
    const key = String(stateCode || '').trim().toUpperCase();
    return STATE_ZONES[key] || 'Australia/Sydney';
  }

  /* Weekday + zero-padded HH:MM as the given IANA zone sees them right
     now. `hourCycle: 'h23'` rather than `hour12: false` — the latter
     reports midnight as "24" in some engines. */
  /* One formatter per zone, reused. Constructing an Intl.DateTimeFormat
     is ~16x the cost of calling an existing one (0.043ms vs 0.003ms
     measured), and the store network resolves to only three or four
     distinct zones, so the cache stays tiny. */
  const zoneFormatters = new Map();

  function zoneFormatter(timeZone) {
    let formatter = zoneFormatters.get(timeZone);
    if (!formatter) {
      formatter = new Intl.DateTimeFormat('en-AU', {
        timeZone,
        weekday: 'long',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      });
      zoneFormatters.set(timeZone, formatter);
    }
    return formatter;
  }

  function nowInZone(timeZone) {
    const parts = zoneFormatter(timeZone).formatToParts(new Date());
    const part = (type) => (parts.find((p) => p.type === type) || {}).value || '';
    return { day: part('weekday').toLowerCase(), time: part('hour') + ':' + part('minute') };
  }

  /* 'HH:MM' -> customer-facing '9am' / '5:30pm'. The metaobject stores
     24-hour times; nothing customer-facing should render them raw. */
  function displayTime(hhmm) {
    /* Match the shape before reading it. Splitting first is the trap:
       ''.split(':').map(Number) is [0], which is finite, so a blank
       value would format as a confident "12am". */
    const match = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm).trim());
    if (!match) return '';
    const h = Number(match[1]);
    const m = Number(match[2]);
    if (h > 23 || m > 59) return '';
    const hour = h % 12 === 0 ? 12 : h % 12;
    return (m ? hour + ':' + String(m).padStart(2, '0') : String(hour)) + (h < 12 ? 'am' : 'pm');
  }

  window.theme = window.theme || {};
  window.theme.zoneForState = zoneForState;
  window.theme.nowInZone = nowInZone;
  window.theme.displayTime = displayTime;
  window.theme.loadProductJson = loadProductJson;
  window.theme.formatMoney = formatMoney;
  window.theme.cartQueue = cartQueue;
  window.theme.cartAdd = cartAdd;
  window.theme.publish = publish;
  window.theme.observeOnce = observeOnce;
  window.theme.trackingItem = trackingItem;
  window.theme.trackingItems = trackingItems;
  window.theme.resolveTrackList = resolveTrackList;
  window.theme.trackList = trackList;
  window.theme.trackSelectItem = trackSelectItem;
  window.theme.trackAtcContext = trackAtcContext;
})();

/* ─── Product-list tracking behaviour ─────────────────────────────────
   Wires the shared list-tracking helpers to the DOM once per page:
   - observes every `[data-track-list-id]` wrapper for view_item_list;
   - delegates product-card navigation clicks to select_item.
   Quick-add select_item / atc_context are emitted from the quick-add
   flow itself (quick-add-drawer), where the add mutation lives. */
(function () {
  function scanLists(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-track-list-id]').forEach((el) => window.theme.trackList(el));
  }

  function onCardNavClick(event) {
    const card = event.target.closest('[data-product-card]');
    if (!card) return;
    /* Only genuine navigation to the PDP — not the quick-add or wishlist
       controls, which have their own instrumentation. */
    if (event.target.closest('[data-quick-add-trigger], [data-wishlist-button]')) return;
    const link = event.target.closest('a[href]');
    if (!link || !card.contains(link)) return;
    window.theme.trackSelectItem(card);
  }

  /* select_promotion on any banner click, excluding the slideshow's own
     pagination controls (navigating slides is not a promotion engagement).
     view_promotion is emitted per-surface because it must respect slide
     visibility. */
  function onPromoClick(event) {
    const promo = event.target.closest('[data-track-promo-id]');
    if (!promo) return;
    if (event.target.closest('[data-hero-dots]')) return;
    window.theme.publish('eckersleys:select_promotion', {
      promotion_id: promo.dataset.trackPromoId,
      promotion_name: promo.dataset.trackPromoName || '',
    });
  }

  /* add_to_wishlist. Every add — guest, signed-in, or via the list picker
     — starts with a heart click, so capture the clicked button's product
     data and emit on the resulting `added` toast (which is dispatched on
     document without a button reference). */
  let lastWishlistItem = null;
  function onWishlistClick(event) {
    const btn = event.target.closest('[data-wishlist-button]');
    if (!btn) return;
    const d = btn.dataset;
    const price = parseFloat(d.wishlistProductPrice);
    lastWishlistItem = {
      index: 0,
      quantity: 1,
      item_id: d.wishlistVariantSku || d.wishlistVariantId || d.wishlistProductId || null,
      item_name: d.wishlistProductTitle || '',
      item_brand: d.wishlistVendor || '',
      item_variant: d.wishlistVariantTitle || '',
      item_category: '',
      affiliation: 'Online Store',
    };
    if (Number.isFinite(price)) lastWishlistItem.price = price;
  }
  function onWishlistToast(event) {
    const detail = event.detail || {};
    if (detail.kind !== 'added' || !lastWishlistItem) return;
    const item = Object.assign({}, lastWishlistItem);
    const currency = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
    window.theme.publish('eckersleys:add_to_wishlist', {
      currency: currency || undefined,
      value: item.price,
      items: [item],
    });
  }

  function init() {
    scanLists(document);
    /* Capture phase so select_item / select_promotion publish before the
       navigation starts. */
    document.addEventListener('click', onCardNavClick, true);
    document.addEventListener('click', onPromoClick, true);
    document.addEventListener('click', onWishlistClick, true);
    document.addEventListener('wishlist:toast', onWishlistToast);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Surfaces that inject cards after fetch (recommendations, quick views)
     re-scan by calling window.theme.trackList(el) directly, or dispatch
     `theme:track-scan` with { detail: { root } } to rescan a subtree. */
  document.addEventListener('theme:track-scan', (event) => {
    scanLists(event.detail && event.detail.root);
  });
})();

/* ─── Web Vitals ──────────────────────────────────────────────────────
   Self-contained LCP / CLS / INP collector — the theme has no bundler for
   assets, so rather than vendor the web-vitals library this implements the
   metrics off the standard Performance APIs. LCP + CLS follow the official
   algorithms; INP is approximated as the worst interaction latency (close
   to the library's p98 for typical sessions). Each metric publishes once
   when it finalises; INP/CLS finalise at page-hide, so some reports race
   teardown — emitted immediately and that loss is accepted. */
(function () {
  if (!('PerformanceObserver' in window)) return;

  const pageType = (document.body && document.body.getAttribute('data-page-type')) || '';
  const RATINGS = { LCP: [2500, 4000], INP: [200, 500], CLS: [0.1, 0.25] };
  const reported = {};
  let idCounter = 0;

  function rating(name, value) {
    const t = RATINGS[name];
    if (!t) return '';
    if (value <= t[0]) return 'good';
    if (value <= t[1]) return 'needs-improvement';
    return 'poor';
  }

  function reportOnce(name, rawValue) {
    if (reported[name]) return;
    reported[name] = true;
    if (!window.theme || typeof window.theme.publish !== 'function') return;
    idCounter += 1;
    /* CLS ×1000 so it survives integer rounding; LCP/INP are ms. */
    const value = Math.round(name === 'CLS' ? rawValue * 1000 : rawValue);
    window.theme.publish('eckersleys:web_vitals', {
      metric_name: name,
      metric_value: value,
      metric_rating: rating(name, rawValue),
      metric_id: 'v1-' + name + '-' + Date.now() + '-' + Math.floor(Math.random() * 1e9) + '-' + idCounter,
      page_type: pageType,
    });
  }

  function observe(type, cb, opts) {
    try {
      const po = new PerformanceObserver((list) => cb(list.getEntries(), po));
      po.observe(Object.assign({ type: type, buffered: true }, opts || {}));
      return po;
    } catch (e) {
      return null;
    }
  }

  /* LCP — last entry, finalised on first interaction or page hide. */
  let lcpValue = 0;
  const lcpPO = observe('largest-contentful-paint', (entries) => {
    const last = entries[entries.length - 1];
    if (last) lcpValue = last.renderTime || last.loadTime || last.startTime || 0;
  });
  function finalizeLCP() {
    if (lcpPO) lcpPO.disconnect();
    if (lcpValue > 0) reportOnce('LCP', lcpValue);
  }
  ['keydown', 'click', 'pointerdown'].forEach((t) =>
    addEventListener(t, finalizeLCP, { once: true, capture: true })
  );

  /* CLS — session-window max (5s cap, 1s gap between shifts). */
  let clsValue = 0;
  let sessionValue = 0;
  let sessionFirst = 0;
  let sessionLast = 0;
  observe('layout-shift', (entries) => {
    entries.forEach((entry) => {
      if (entry.hadRecentInput) return;
      if (sessionValue && entry.startTime - sessionLast < 1000 && entry.startTime - sessionFirst < 5000) {
        sessionValue += entry.value;
        sessionLast = entry.startTime;
      } else {
        sessionValue = entry.value;
        sessionFirst = entry.startTime;
        sessionLast = entry.startTime;
      }
      if (sessionValue > clsValue) clsValue = sessionValue;
    });
  });

  /* INP — worst interaction latency across event-timing entries. */
  let inpValue = 0;
  observe('event', (entries) => {
    entries.forEach((entry) => {
      if (entry.interactionId && entry.duration > inpValue) inpValue = entry.duration;
    });
  }, { durationThreshold: 40 });

  function finalize() {
    finalizeLCP();
    reportOnce('CLS', clsValue);
    if (inpValue > 0) reportOnce('INP', inpValue);
  }
  addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') finalize();
  });
  addEventListener('pagehide', finalize, { capture: true });
})();
